/**
 * Kubernetes quantity suffixes. Binary suffixes step by 1024, decimal ones by
 * 1000, and the fractional decimal suffixes cover CPU values such as "250m".
 */
const SUFFIX_MULTIPLIERS: Record<string, number> = {
  Ki: 1024,
  Mi: 1024 ** 2,
  Gi: 1024 ** 3,
  Ti: 1024 ** 4,
  Pi: 1024 ** 5,
  Ei: 1024 ** 6,
  n: 1e-9,
  u: 1e-6,
  m: 1e-3,
  k: 1e3,
  M: 1e6,
  G: 1e9,
  T: 1e12,
  P: 1e15,
  E: 1e18,
};

/**
 * Reads a Kubernetes quantity as a number.
 *
 * Used and hard values for one resource always carry the same unit, so a ratio
 * of the two is right whatever that unit is. The absolute number is only
 * meaningful against another quantity of the same resource.
 */
export function parseQuantity(quantity: string | undefined): number {
  if (!quantity) {
    return 0;
  }

  const match = quantity.match(/^(-?\d+(?:\.\d+)?)([A-Za-z]*)$/);
  if (!match) {
    return 0;
  }

  const value = parseFloat(match[1]);
  const multiplier = match[2] ? SUFFIX_MULTIPLIERS[match[2]] : 1;

  return multiplier === undefined ? value : value * multiplier;
}

/**
 * How full a limit is, from 0 to 1. A hard value of zero forbids the resource
 * outright, which reads as full rather than as a division by zero.
 */
export function usageRatio(used: string, hard: string): number {
  const hardValue = parseQuantity(hard);

  if (hardValue <= 0) {
    return 1;
  }

  return Math.min(parseQuantity(used) / hardValue, 1);
}
